import { Injectable } from '@angular/core';
import { Observable, throwError, BehaviorSubject, firstValueFrom } from 'rxjs';
import { HttpsService } from './https.service';
import { map, catchError } from 'rxjs/operators';
import { StorageService } from '../../utils/storage.service';
import { apiConfig } from 'src/environments/environment';
import { RSCBase } from 'src/app/controllers/base/RSCBase';
import { deepFind } from 'src/app/controllers/base/RSCUtils';
import { RSCUser } from 'src/app/controllers/RSCUser';
import { IAPIUserInformationDef } from 'src/app/interfaces/user';
import {
  SystemSupportedAccountTypes,
  profileTypes,
} from 'src/environments/constants';

const loggedOutUserInfo: IAPIUserInformationDef = {
  user: { id: 0 },
} as IAPIUserInformationDef;
const userInformation$: BehaviorSubject<IAPIUserInformationDef> =
  new BehaviorSubject<IAPIUserInformationDef>(loggedOutUserInfo as any);
const authToken$: BehaviorSubject<string> = new BehaviorSubject<string>('');
const initialized$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
  false,
);

export const userInformation: Observable<IAPIUserInformationDef> =
  userInformation$.asObservable();
export const initialized = initialized$.asObservable();

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  requests: any = {};
  constructor(
    private storage: StorageService,
    public http: HttpsService,
  ) {
    if (apiConfig.IS_APP) {
      this.loadAuthToken();
    } else {
      this.loadUserInfo();
    }
  }

  private loadAuthToken() {
    this.storage.get('authToken').then((storedToken) => {
      if (storedToken) {
        authToken$.next(storedToken);
      }
      authToken$.subscribe({
        next: (token) => this.storage.set('authToken', token),
      });
      this.loadUserInfo();
    });
  }

  private loadUserInfo() {
    this.storage.get('userInfo').then((storedUserInfo) => {
      if (storedUserInfo) {
        userInformation$.next(storedUserInfo);
      }
      userInformation$.subscribe({
        next: (userInfo) => {
          this.storage.set('userInfo', userInfo);
          //get account type
          let retVal = 'none';
          for (let type in SystemSupportedAccountTypes) {
            if (
              deepFind(
                userInformation$.value,
                (SystemSupportedAccountTypes as any)[type].path,
              ) > 0
            ) {
              retVal = type;
              break;
            }
          }
          RSCBase.setAccountType(retVal); // set account type
          RSCUser.SetCurrentUser({ fillWith: userInfo }); // set account type
          if (!initialized$.value) {
            initialized$.next(true);
          }
        },
      });
    });
  }

  private signAndSecureRequest(
    callback: any,
    endPoint: any,
    data: any,
    options: any,
    version: any,
  ) {
    const requestId = data?.__$id;
    if (requestId) {
      delete data.__$id;
    }
    options = options || {};
    options.observe = 'response';
    options.headers = Object.assign({}, options.headers || {}, {
      // 'Content-Type': (options.headers || {})['Content-Type'] || (apiConfig.DEFAULT_CONTENT_TYPE || 'multipart/form-data')
    });
    options.responseType =
      options.responseType || apiConfig.DEFAULT_RESPONSE_TYPE || 'json';
    let suscription: any;
    return new Observable((subscriber) => {
      suscription = initialized$.subscribe({
        next: (value) => {
          if (value === true) {
            setTimeout(() => suscription.unsubscribe(), 10);
            if (apiConfig.IS_APP) {
              options.headers = Object.assign({}, options.headers || {}, {
                apiaf: 'app',
              });
              if ((authToken$.value || '').length > 0) {
                options.headers = Object.assign({}, options.headers || {}, {
                  Authorization: 'Bearer ' + authToken$.value,
                });
              }
              if (profileTypes.admin) {
                options.headers = Object.assign({}, options.headers || {}, {
                  'Account-type': profileTypes.admin,
                });
              }
            }
            // here call callback
            const request = callback(endPoint, data, options, version)
              .pipe(
                map((response: any) => {
                  if (
                    endPoint === 'account/oauth_login_complete' ||
                    endPoint === 'account/login' ||
                    endPoint === 'account/check_login' ||
                    endPoint === 'account/signup'
                  ) {
                    if (response.app_token) {
                      authToken$.next(response.app_token.oauth_token.token);
                      delete response.app_token;
                    }
                    userInformation$.next(response);
                  } else if (endPoint === 'account/logout') {
                    this._resetAuthState();
                  }
                  return response;
                }),
                catchError((error) => {
                  error = error.error?.result || error;
                  if (
                    /* error instanceof Error &&  */ error.message ===
                      'YOU_ARE_NOT_LOGGED_IN' ||
                    (error as any).msg === 'YOU_ARE_NOT_LOGGED_IN'
                  ) {
                    const url = new URL(window.location.toString());
                    url.searchParams.set(
                      'return_to_url',
                      encodeURI(window.location.pathname),
                    );
                    window.history.pushState({}, '', url);
                    this._resetAuthState();
                  }
                  return throwError(error);
                }),
              )
              .subscribe({
                next: (result: any) => subscriber.next(result),
                error: (error: any) => subscriber.error(error),
                complete: () => {
                  subscriber.complete();
                  delete this.requests[requestId];
                },
              });

            if (requestId) {
              this.requests[requestId] = request;
            }
          }
        },
      });
    });
  }

  _get(endPoint: any, data?: any, options?: any, version?: any) {
    return this.signAndSecureRequest(
      (_endPoint: any, _data: any, _options: any, _version: any) =>
        this.http._get(
          (_version ? _version + '/' : '') + _endPoint,
          _data,
          _options,
        ),
      endPoint,
      data,
      options,
      version,
    ) as Observable<any>;
  }

  _post(endPoint: any, data: any, options?: any, version?: any) {
    return this.signAndSecureRequest(
      (_endPoint: any, _data: any, _options: any, _version: any) =>
        this.http._post(
          (_version ? _version + '/' : '') + _endPoint,
          _data,
          _options,
        ),
      endPoint,
      data,
      options,
      version,
    ) as Observable<any>;
  }

  _resetAuthState() {
    userInformation$.next(loggedOutUserInfo as any);
    authToken$.next('');
  }

  get AuthorizationToken() {
    return authToken$.value || '';
  }

  CancelRequest(requestId: string) {
    if (this.requests[requestId]) {
      this.requests[requestId]?.unsubscribe();
      delete this.requests[requestId];
    }
  }
}
